import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { Alert } from '@/shared/ui/Alert';
import { Card, CardBody } from '@/shared/ui/Card';
import { LogoLockup } from '@/shared/ui/Logo';

import { mensagemDoGoogle, resultadoDoGoogle } from './backupLabels';

/**
 * Chegada da janela do Google.
 *
 * O servidor manda para ca depois de tratar o retorno do Google, com um codigo de
 * resultado no endereco. Esta tela abre dentro da janela que a tela de backups
 * abriu, e por isso fica fora da sessao: a sessao vive na memoria da aba de
 * origem, e esta janela nao a tem.
 *
 * Ela so faz duas coisas: avisa a aba de origem, pela mensagem entre janelas da
 * mesma origem, e se fecha. Quando nao ha aba de origem para avisar, mostra o
 * resultado e o caminho de volta. Nada aqui consulta o servidor.
 */
export function GoogleReturnPage() {
  const [parametros] = useSearchParams();
  const codigo = parametros.get('google');
  const resultado = resultadoDoGoogle(codigo);
  // A janela de origem e lida uma vez, na montagem: e ela que decide o texto.
  const [origem] = useState<Window | null>(() => {
    const aberta = window.opener as Window | null;
    return aberta && !aberta.closed ? aberta : null;
  });
  const avisou = useRef(false);

  useEffect(() => {
    if (avisou.current || !origem) return;
    avisou.current = true;
    try {
      // O destino e a propria origem: a mensagem nao sai para nenhuma outra pagina,
      // mesmo que a janela de origem tenha navegado para outro site.
      origem.postMessage(
        { tipo: mensagemDoGoogle, resultado: codigo ?? '' },
        window.location.origin,
      );
      window.close();
    } catch {
      // Sem acesso a janela de origem, a tela mostra o resultado e o link de volta.
    }
  }, [codigo, origem]);

  const avisada = origem !== null;

  return (
    <div className="mx-auto flex min-h-dvh max-w-lg flex-col items-center justify-center gap-6 px-4">
      <LogoLockup name="SeguraCore" tagline="Administracao da plataforma" />
      <Card className="w-full">
        <CardBody className="flex flex-col gap-4">
          {resultado ? (
            <Alert tone={resultado.tom} title={resultado.titulo}>
              {resultado.texto}
            </Alert>
          ) : (
            <Alert tone="warning" title="Resultado desconhecido">
              Volte para a tela de backups para ver a situacao do Google Drive.
            </Alert>
          )}
          <p className="text-sm text-ink-600">
            {avisada
              ? 'A tela de backups ja recebeu o resultado. Pode fechar esta janela.'
              : 'Pode fechar esta janela e voltar para a tela de backups.'}
          </p>
          <Link
            to={
              codigo
                ? `/admin/backups?google=${encodeURIComponent(codigo)}`
                : '/admin/backups'
            }
            className="text-sm font-medium text-brand-600 hover:underline"
          >
            Ir para a tela de backups
          </Link>
        </CardBody>
      </Card>
    </div>
  );
}
